// インポート/エクスポート機能のためのユーティリティ関数
import { exportAllData, importAllData } from './storage';

/**
 * データをJSONファイルとしてエクスポートする
 * @returns {boolean} エクスポートが成功したかどうか
 */
export const exportDataToFile = () => {
  try {
    // すべてのデータを取得
    const data = exportAllData();
    if (!data) return false;
    
    // JSONに変換
    const jsonString = JSON.stringify(data, null, 2);
    
    // Blobを作成
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // ダウンロードリンクを作成
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-chat-data-${new Date().toISOString().slice(0, 10)}.json`;
    
    // リンクをクリックしてダウンロード開始
    document.body.appendChild(link);
    link.click();
    
    // クリーンアップ
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('データのエクスポートに失敗しました:', error);
    return false;
  }
};

/**
 * JSONファイルからデータをインポートする
 * @param {File} file - インポートするJSONファイル
 * @param {Object} options - インポートオプション
 * @param {boolean} options.overwriteExisting - 既存データを上書きするかどうか（デフォルト: false）
 * @returns {Promise<{success: boolean, message: string, skippedItems?: Object}>} インポート結果
 */
export const importDataFromFile = (file, options = { overwriteExisting: false }) => {
  // Fileオブジェクトをoptions.fileに必ずセット
  options.file = file;
  return new Promise((resolve, reject) => {
    try {
      if (!file) {
        resolve({ success: false, message: 'ファイルが選択されていません' });
        return;
      }
      
      if (file.type !== 'application/json') {
        resolve({ success: false, message: 'JSONファイルを選択してください' });
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          
          // デバッグ情報を表示
          console.log('インポートされたJSONデータ:', JSON.stringify(jsonData, null, 2).substring(0, 500) + '...');
          console.log('JSONデータのタイプ:', typeof jsonData);
          console.log('JSONデータのキー:', Object.keys(jsonData));
          
          // データの検証 - より柔軟に対応
          // 有効なJSONであれば、可能な限りインポートを試みる
          const validData = {};
          
          // データが配列の場合の処理
          if (Array.isArray(jsonData)) {
            console.log('データは配列形式です');
            // 配列の場合、会話配列として処理
            validData.conversations = jsonData;
          } else {
            // オブジェクトの場合の処理
            
            // conversationsの検証
            if (jsonData.conversations && Array.isArray(jsonData.conversations)) {
              validData.conversations = jsonData.conversations;
              console.log('conversationsを検出しました (配列)');
            } else if (jsonData.conversations) {
              // 配列でない場合は、配列に変換を試みる
              try {
                validData.conversations = [jsonData.conversations];
                console.log('conversationsを配列に変換しました');
              } catch (e) {
                console.warn('conversationsの変換に失敗しました:', e);
              }
            } else {
              // conversationsプロパティがない場合、オブジェクト自体が会話データか確認
              if (jsonData.id && jsonData.title) {
                validData.conversations = [jsonData];
                console.log('オブジェクト自体を会話データとして処理しました');
              }
            }
            
            // themesの検証
            if (jsonData.themes && Array.isArray(jsonData.themes)) {
              validData.themes = jsonData.themes;
              console.log('themesを検出しました (配列)');
            } else if (jsonData.themes) {
              try {
                validData.themes = [jsonData.themes];
                console.log('themesを配列に変換しました');
              } catch (e) {
                console.warn('themesの変換に失敗しました:', e);
              }
            }
            
            // tagsの検証
            if (jsonData.tags && Array.isArray(jsonData.tags)) {
              validData.tags = jsonData.tags;
              console.log('tagsを検出しました (配列)');
            } else if (jsonData.tags) {
              try {
                validData.tags = [jsonData.tags];
                console.log('tagsを配列に変換しました');
              } catch (e) {
                console.warn('tagsの変換に失敗しました:', e);
              }
            }
            
            // favoritesの検証
            if (jsonData.favorites && Array.isArray(jsonData.favorites)) {
              validData.favorites = jsonData.favorites;
              console.log('favoritesを検出しました (配列)');
            } else if (jsonData.favorites) {
              try {
                validData.favorites = [jsonData.favorites];
                console.log('favoritesを配列に変換しました');
              } catch (e) {
                console.warn('favoritesの変換に失敗しました:', e);
              }
            }
            
            // settingsの検証
            if (jsonData.settings && typeof jsonData.settings === 'object') {
              validData.settings = jsonData.settings;
              console.log('settingsを検出しました');
            }
          }
          
          // 少なくとも1つのデータ型が有効であれば処理を続行
          if (Object.keys(validData).length === 0) {
            console.warn('有効なデータが見つかりませんでした');
            resolve({ success: false, message: '有効なデータが見つかりませんでした。ファイル形式を確認してください。' });
            return;
          } else {
            console.log('有効なデータを検出しました:', Object.keys(validData));
          }
          
          // データのインポート
          // ファイル名を取得して渡す
          // ファイル名が取得できない場合は警告を表示するが、処理は続行する
          let filename = 'インポートデータ';
          if (options.file && options.file.name) {
            filename = options.file.name;
          } else {
            console.warn('ファイル名が取得できません。デフォルト名を使用します。');
          }
          console.log('インポートする実際のファイル名:', filename);
          console.log('ファイルオブジェクト:', options.file);
          
          const result = importAllData(validData, options.overwriteExisting, filename);
          
          if (result.success) {
            const message = result.skippedItems && Object.keys(result.skippedItems).length > 0
              ? `データのインポートに成功しました。既存データとの重複により一部のアイテムをスキップしました。`
              : 'データのインポートに成功しました';
            
            resolve({ 
              success: true, 
              message: message,
              data: jsonData,
              skippedItems: result.skippedItems
            });
          } else {
            resolve({ success: false, message: 'データのインポートに失敗しました' });
          }
        } catch (error) {
          console.error('JSONの解析に失敗しました:', error);
          resolve({ success: false, message: 'JSONの解析に失敗しました' });
        }
      };
      
      reader.onerror = () => {
        resolve({ success: false, message: 'ファイルの読み込みに失敗しました' });
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('データのインポートに失敗しました:', error);
      resolve({ success: false, message: 'データのインポートに失敗しました' });
    }
  });
};
